import clsx from 'clsx';
import { useTopic } from 'hooks/useTopic';
import { FC, Fragment } from 'react';
import { Answer, Task } from 'types/elearning';
import { DropZone } from './drop-zone';
import { ImagePlaceholder } from './image-placeholder';
import cls from './match-text-with-description.module.scss';
import { useSelector } from 'react-redux';
import { selectTaskAnswerById } from 'store/topic/topicSlice';
import { useMatchTextWIthImages } from './useMatchTextWIthImages';

interface IProps {
    task: Task;
    isChecked: boolean;
    correctAnswer: Answer['correct_answer'];
    background?: string;
}

export const MatchTextWithImages: FC<IProps> = ({ task, isChecked, background }) => {
    const taskAnswer = useSelector(selectTaskAnswerById(task.id));
    const {isCorrect} = useMatchTextWIthImages(taskAnswer);

    const {
        matchTextWithDescriptionCurrentAnswer,
        handleSetMatchTextWithDescriptionCurrentAnswer,
    } = useTopic();

    const clsWrapper = clsx(cls.wrapper, {
        [cls.wrapperDisabled]: isChecked,
    });


    const coordinates = [
        { x: 48, y: 5.2, hoverText: 'Moment 1. Przed kontaktem z pacjentem', finishText: 'Standardowa opieka – ochrona pacjenta', number: 1},
        { x: 79, y: 42, hoverText: 'Moment 2. Przed czystą/aseptyczną procedurą', finishText: 'Chroni pacjenta przed infekcją', number: 2},
        { x: 71, y: 66.5, hoverText: 'Moment 3. Po kontakcie z płynami ustrojowymi', finishText: 'Próbka moczu może być źródłem kontaminacji', number: 3},
        { x: 33, y: 53, hoverText: 'Moment 4. Po kontakcie z pacjentem', finishText: 'Po kontakcie z pacjentem', number: 4},
        { x: 21, y: 23, hoverText: 'Moment 5. Po kontakcie z otoczeniem pacjenta', finishText: 'Otoczenie = rezerwuar drobnoustrojów', number: 5}
    ];

    const descriptions = task?.content?.descriptions ?? {};
    const sortedEntries = Object.entries(descriptions)
        .sort((a, b) => {
            const numA = parseInt(a[0].replace('desciption_', ''));
            const numB = parseInt(b[0].replace('desciption_', ''));
            return numA - numB;
    });

    const normalized = sortedEntries.reduce((acc, [_, value], index) => {
        acc[`item_${index + 1}`] = value;
        return acc;
    }, {});

    const texts = task?.content?.texts ?? {};
    const sortedEntriesText = Object.entries(texts)
        .sort((a, b) => {
            const numA = parseInt(a[0].replace('item_', ''));
            const numB = parseInt(b[0].replace('item_', ''));
            return numA - numB;
    });

    const normalizedText = sortedEntriesText.reduce((acc, [_, value], index) => {
        acc[`desciption_${index + 1}`] = value;
        return acc;
    }, {});

    return (
        <div className={clsWrapper}>
            <div className={cls.dropArea}>
                {background && <img src={background} />}
                {Object?.entries?.(normalizedText)?.map?.(
                    ([key, description], id) => {
                        const correctId = Number((description as string).replace('MOMENT ', '')) - 1;
                        const image_url = normalizedText?.[
                            matchTextWithDescriptionCurrentAnswer?.[key]
                        ] as string;
                        
                        return (
                            <Fragment
                                key={`${key}-${description}-${id}-${image_url}`}
                            >
                                <DropZone
                                    id={key}
                                    description={id + 1 + ''}
                                    image_url={image_url}
                                    coordinate={coordinates[correctId]}
                                    isCorrect={isCorrect(key)}
                                    image={
                                        Object?.entries?.(
                                            normalized
                                        )?.find?.(
                                            ([imgKey]) => {
                                                return imgKey ===
                                                matchTextWithDescriptionCurrentAnswer?.[
                                                    key
                                                ]
                                            }
                                        ) as [string, string]
                                    }
                                    handleDrop={
                                        handleSetMatchTextWithDescriptionCurrentAnswer
                                    }
                                />
                            </Fragment>
                        );
                    }
                )}
            </div>
            <div className={cls.imagesArea}>
                {Object?.entries?.(normalized)?.map?.(
                    ([key, text], id) => (
                        <Fragment key={`${key}-${text}-${id}`}>
                            <ImagePlaceholder
                                id={key}
                                text={text as string}
                                imgSrc={task?.content?.images[`item_${key.split('_')[1]}`] as string}
                                isCorrect={isCorrect(key.replace('item_', 'desciption_'))}
                                isEmpty={
                                    matchTextWithDescriptionCurrentAnswer &&
                                    Object?.values?.(
                                        matchTextWithDescriptionCurrentAnswer
                                    )?.includes?.(key)
                                }
                                feedback={coordinates[Number(key.replace('item_', '')) - 1]?.finishText}
                            />
                        </Fragment>
                    )
                )}
            </div>
        </div>
    );
};
